import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  STATUS_LABEL,
  STATUS_STYLE,
  formatAge,
  formatBytes,
  formatDateTime,
} from "../lib/payoutSetup";

const INPUT_CLASS = "h-10 rounded-md border-zinc-200 focus-visible:ring-black";
const FILTERS = ["open", "all", "submitted", "needs_changes", "account_created", "under_review", "activated", "connected", "rejected"];
const STATUS_RANK = {
  rejected: -1,
  submitted: 0,
  needs_changes: 0,
  account_created: 1,
  under_review: 2,
  activated: 3,
  connected: 4,
};

async function copyText(value, label) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy. Select the text and copy it manually.");
  }
}

// Errors from blob requests arrive as a Blob; read the JSON detail out of it.
async function blobErrorMessage(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.detail) return String(parsed.detail);
    } catch {
      /* fall through */
    }
  }
  return formatApiError(err);
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${
        STATUS_STYLE[status] || STATUS_STYLE.submitted
      }`}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function CopyRow({ label, hint, value, sensitive, masked, revealedValue, testId }) {
  const shown = sensitive ? revealedValue || masked : value;
  const copyable = Boolean(sensitive ? revealedValue : value);
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-100 py-2 last:border-0"
      data-testid={testId}
    >
      <div className="w-full text-xs text-zinc-500 sm:w-48 sm:shrink-0">
        {label}
        {hint && <span className="block text-[11px] text-zinc-400">{hint}</span>}
      </div>
      <div
        className={`min-w-0 flex-1 break-all text-sm ${
          sensitive ? "font-mono" : ""
        } ${shown ? "text-zinc-900" : "text-zinc-400"}`}
      >
        {shown || "—"}
        {sensitive && !revealedValue && masked && (
          <span className="ml-2 font-sans text-[11px] text-zinc-400">masked</span>
        )}
      </div>
      <button
        type="button"
        disabled={!copyable}
        onClick={() => copyText(sensitive ? revealedValue : value, label)}
        className="rounded-md border border-zinc-200 p-1.5 text-zinc-500 hover:border-zinc-400 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-30"
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Panel({ title, subtitle, action, children, testId }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-zinc-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function FounderChecklist({ detail, webhookReady }) {
  const rank = STATUS_RANK[detail.status] ?? 0;
  const items = [
    ["In the Razorpay Partner dashboard, switch Test Mode off.", rank >= 1],
    ["Open Client Onboarding → Add New Account.", rank >= 1],
    ["Fill in the new account and its KYC form with the details below (same order) and upload the documents.", rank >= 1],
    ["Back here, mark “Razorpay account created” with the account's acc_ id.", rank >= 1],
    ["Wait for Razorpay to activate it. Mark Under review, then Activated, as it moves.", rank >= 3],
    ["Open the client's account and generate live API keys (Account & Settings → API Keys).", rank >= 4],
    ["Generate a webhook secret below and add the webhook (URL + payment.captured) in the client's account.", rank >= 4 || webhookReady],
    ["Connect the keys here. The organizer is emailed automatically.", rank >= 4],
  ];
  return (
    <Panel title="Your steps" subtitle="Razorpay Partner dashboard → this screen" testId="founder-checklist">
      <ol className="space-y-1.5">
        {items.map(([text, done], index) => (
          <li key={text} className="flex gap-2 text-sm">
            {done ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300" />
            )}
            <span className={done ? "text-zinc-400 line-through" : "text-zinc-800"}>
              {index + 1}. {text}
            </span>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function describeAudit(entry) {
  const who = entry.actor === "organizer" ? "Organizer" : entry.actor_id;
  const label = (s) => STATUS_LABEL[s] || s;
  switch (entry.action) {
    case "submitted":
      return `${who} submitted the request`;
    case "resubmitted":
      return `${who} resubmitted${
        entry.changed_fields?.length ? ` (changed: ${entry.changed_fields.join(", ")})` : ""
      }`;
    case "status_changed":
      return `${who}: ${label(entry.from_status)} → ${label(entry.to_status)}${
        entry.razorpay_account_id ? ` (${entry.razorpay_account_id})` : ""
      }`;
    case "revealed":
      return `${who} revealed ${(entry.fields || []).join(", ")}`;
    case "document_viewed":
      return `${who} opened document: ${entry.document}`;
    case "documents_purged":
      return `${who} deleted ${entry.files_deleted ?? 0} uploaded file(s)`;
    case "webhook_secret_generated":
      return `${who} generated a webhook secret`;
    case "gateway_connected":
      return `${who} connected keys ${entry.key_id_masked || ""} (${entry.key_env || "?"})`;
    default:
      return `${who}: ${entry.action}`;
  }
}

function RequestDetail({ organizerId, onBack, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: "", note: "", razorpay_account_id: "" });
  const [savingStatus, setSavingStatus] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [connectForm, setConnectForm] = useState({ key_id: "", key_secret: "", webhook_secret: "" });
  const [connecting, setConnecting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [opening, setOpening] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/super-admin/payout-setup/${organizerId}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [organizerId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    },
    [preview]
  );

  const applyDetail = (data) => {
    setDetail(data);
    onChanged();
  };

  const reveal = async () => {
    setRevealing(true);
    try {
      const { data } = await api.post(`/super-admin/payout-setup/${organizerId}/reveal`);
      setRevealed(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRevealing(false);
    }
  };

  const openDocument = async (doc, download) => {
    setOpening(doc.id);
    try {
      const response = await api.get(
        `/super-admin/payout-setup/${organizerId}/documents/${doc.id}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = doc.filename || "document";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        setPreview({ url, type: doc.content_type, name: doc.filename, id: doc.id });
      }
      load();
    } catch (err) {
      toast.error(await blobErrorMessage(err));
    } finally {
      setOpening(null);
    }
  };

  const saveStatus = async (e) => {
    e.preventDefault();
    if (!statusForm.status) {
      toast.error("Choose the new status");
      return;
    }
    setSavingStatus(true);
    try {
      const body = { status: statusForm.status, note: statusForm.note.trim() };
      if (statusForm.razorpay_account_id.trim()) {
        body.razorpay_account_id = statusForm.razorpay_account_id.trim();
      }
      const { data } = await api.post(`/super-admin/payout-setup/${organizerId}/status`, body);
      applyDetail(data);
      setStatusForm({ status: "", note: "", razorpay_account_id: "" });
      toast.success(`Moved to ${STATUS_LABEL[data.status]}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingStatus(false);
    }
  };

  const generateWebhookSecret = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/super-admin/payout-setup/${organizerId}/webhook-secret`);
      setWebhook(data);
      setConnectForm((f) => ({ ...f, webhook_secret: data.webhook_secret }));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const connectKeys = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      const body = { key_id: connectForm.key_id.trim(), key_secret: connectForm.key_secret.trim() };
      if (connectForm.webhook_secret.trim()) body.webhook_secret = connectForm.webhook_secret.trim();
      const { data } = await api.post(
        `/super-admin/payout-setup/${organizerId}/connect-gateway`,
        body
      );
      applyDetail(data);
      setConnectForm({ key_id: "", key_secret: "", webhook_secret: "" });
      toast.success("Keys verified with Razorpay and connected.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConnecting(false);
    }
  };

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post(`/super-admin/payout-setup/${organizerId}/purge-documents`);
      applyDetail(data);
      setConfirmPurge(false);
      setPreview(null);
      toast.success("Uploaded documents deleted. The audit trail is kept.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurging(false);
    }
  };

  if (!detail) {
    return (
      <div className="py-10 text-center text-sm text-zinc-500">
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
        Loading request…
      </div>
    );
  }

  const d = detail.details || {};
  // Values the organizer didn't type: filled with defaults by the server.
  const auto = (key, extra) =>
    [detail.defaulted_fields?.includes(key) ? "auto-filled" : null, extra].filter(Boolean).join(" · ") ||
    undefined;
  const webhookSecret = webhook?.webhook_secret || revealed?.webhook_secret;
  const selectedNeedsNote = ["needs_changes", "rejected"].includes(statusForm.status);
  const gateway = detail.gateway;

  return (
    <div className="space-y-4" data-testid="payout-request-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-2 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="h-4 w-4" /> All requests
          </button>
          <h3 className="text-xl font-semibold text-zinc-900">
            {d.customer_facing_business_name || d.legal_business_name}
          </h3>
          <p className="text-sm text-zinc-500">
            {detail.organizer_email} · submitted {formatDateTime(detail.submitted_at)} (
            {formatAge(detail.age_seconds)} ago)
            {detail.razorpay_account_id && ` · ${detail.razorpay_account_id}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={detail.status} />
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-zinc-200 p-1.5 text-zinc-500 hover:text-zinc-900"
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <FounderChecklist detail={detail} webhookReady={Boolean(detail.webhook?.secret_generated_at)} />

      {detail.changed_fields?.length > 0 && detail.status === "submitted" && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          Changed in the last resubmission: {detail.changed_fields.join(", ")}
        </p>
      )}
      {detail.note && (
        <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-2 text-sm text-zinc-700">
          Note shown to the organizer ({STATUS_LABEL[detail.note_status] || detail.note_status}):{" "}
          {detail.note}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white px-5 py-3">
        <p className="text-sm text-zinc-600">
          PAN, GSTIN and account number are masked. Revealing them is logged with your login and the time.
        </p>
        {revealed ? (
          <Button type="button" variant="outline" onClick={() => setRevealed(null)} className="h-9 rounded-md">
            <EyeOff className="mr-1.5 h-4 w-4" /> Hide
          </Button>
        ) : (
          <Button
            type="button"
            onClick={reveal}
            disabled={revealing}
            className="h-9 rounded-md bg-zinc-900 text-white hover:bg-zinc-700"
            data-testid="reveal-button"
          >
            {revealing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />}
            Reveal for copying
          </Button>
        )}
      </div>

      <Panel title="1 · Add New Account" subtitle="Partner dashboard → Client Onboarding → Add New Account">
        <CopyRow label="Business / account name" value={d.customer_facing_business_name} hint={auto("customer_facing_business_name")} />
        <CopyRow label="Email" value={d.email} />
        <CopyRow label="Phone" value={d.phone} hint="10 digits, without +91" />
      </Panel>

      <Panel title="2 · Contact & business" subtitle="KYC form, first page">
        <CopyRow label="Contact name" value={d.legal_business_name} />
        <CopyRow label="Business type" value={d.business_type_label} hint={auto("business_type", d.business_type)} />
        <CopyRow label="Category" value="Events / ticketing" hint="pick the closest match" />
        <CopyRow label="Business description" value={d.business_model} hint={auto("business_model")} />
      </Panel>

      <Panel title="3 · PAN & tax" subtitle="KYC form, business details">
        <CopyRow label="PAN" sensitive masked={d.pan_masked} revealedValue={revealed?.pan} testId="row-pan" />
        <CopyRow label="Name on PAN" value={d.legal_business_name} />
        <CopyRow label="Billing label (display name)" value={d.customer_facing_business_name} hint={auto("customer_facing_business_name")} />
        {detail.has_gst ? (
          <CopyRow label="GSTIN" sensitive masked={d.gst_masked} revealedValue={revealed?.gst} />
        ) : (
          <CopyRow label="GSTIN" value="" hint="not provided" />
        )}
      </Panel>

      <Panel title="4 · Address" subtitle="Registered and operating address">
        <CopyRow label="Address line 1" value={d.street1} />
        <CopyRow label="Address line 2" value={d.street2} />
        <CopyRow label="City" value={d.city} />
        <CopyRow label="State" value={d.state} />
        <CopyRow label="PIN code" value={d.postal_code} />
      </Panel>

      <Panel title="5 · Bank account" subtitle="Settlement account">
        <CopyRow
          label="Account holder"
          value={d.beneficiary_name}
          hint={
            detail.defaulted_fields?.includes("beneficiary_name")
              ? "auto-filled from name on PAN"
              : undefined
          }
        />
        <CopyRow
          label="Account number"
          sensitive
          masked={d.account_number_masked}
          revealedValue={revealed?.account_number}
          testId="row-account"
        />
        <CopyRow label="IFSC" value={revealed?.ifsc || d.ifsc} />
        <CopyRow
          label="Bank / branch"
          value={[d.bank_name, d.bank_branch].filter(Boolean).join(" · ")}
          hint="from the public IFSC lookup"
        />
      </Panel>

      <Panel
        title="6 · Documents"
        subtitle={
          detail.documents_purged_at
            ? `Deleted ${formatDateTime(detail.documents_purged_at)}`
            : "Opening a document is logged"
        }
        action={
          detail.can_purge_documents &&
          (confirmPurge ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-700">Delete all uploaded files for good?</span>
              <Button
                type="button"
                onClick={purge}
                disabled={purging}
                className="h-8 rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                {purging && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Delete
              </Button>
              <Button type="button" variant="outline" onClick={() => setConfirmPurge(false)} className="h-8 rounded-md">
                Keep
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmPurge(true)}
              className="h-8 rounded-md border-red-200 text-red-700 hover:bg-red-50"
              data-testid="purge-button"
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete documents
            </Button>
          ))
        }
      >
        {!detail.documents?.length ? (
          <p className="text-sm text-zinc-500">No documents.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {detail.documents.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <FileText className="h-4 w-4 text-zinc-400" />
                <span className="font-medium">{doc.slot_label}</span>
                {doc.kind_label && doc.kind_label !== doc.slot_label && (
                  <span className="text-zinc-500">· {doc.kind_label}</span>
                )}
                <span className="min-w-0 truncate text-zinc-400">
                  {doc.filename} · {formatBytes(doc.size)}
                </span>
                {!doc.purged && (
                  <span className="ml-auto flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => openDocument(doc, false)}
                      disabled={opening === doc.id}
                      className="h-8 rounded-md"
                    >
                      {opening === doc.id ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : (
                        <Eye className="mr-1.5 h-4 w-4" />
                      )}
                      Preview
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => openDocument(doc, true)}
                      disabled={opening === doc.id}
                      className="h-8 rounded-md"
                    >
                      <Download className="mr-1.5 h-4 w-4" /> Download
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {preview && (
          <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="document-preview">
            <div className="mb-2 flex items-center justify-between gap-2 text-xs text-zinc-500">
              <span className="truncate">{preview.name}</span>
              <button type="button" onClick={() => setPreview(null)} className="hover:text-zinc-900">
                Close preview
              </button>
            </div>
            {preview.type === "application/pdf" ? (
              <iframe title={preview.name} src={preview.url} className="h-[520px] w-full rounded bg-white" />
            ) : (
              <img src={preview.url} alt={preview.name} className="mx-auto max-h-[520px] rounded" />
            )}
          </div>
        )}
      </Panel>

      <Panel title="Update status" subtitle="The organizer sees the timeline and any note you add">
        <form onSubmit={saveStatus} className="space-y-3">
          <div className="flex flex-wrap gap-3">
            <select
              value={statusForm.status}
              onChange={(e) =>
                setStatusForm({
                  ...statusForm,
                  status: e.target.value,
                  razorpay_account_id:
                    e.target.value === "account_created" ? detail.razorpay_account_id || "" : "",
                })
              }
              className="h-10 min-w-[220px] rounded-md border border-zinc-200 bg-white px-3 text-sm"
              data-testid="status-select"
            >
              <option value="">Move to…</option>
              {detail.allowed_next_statuses.map((option) => (
                <option key={option.status} value={option.status}>
                  {option.label}
                </option>
              ))}
            </select>
            {statusForm.status === "account_created" && (
              <Input
                value={statusForm.razorpay_account_id}
                onChange={(e) => setStatusForm({ ...statusForm, razorpay_account_id: e.target.value.trim() })}
                placeholder="acc_XXXXXXXXXXXXXX"
                className={`${INPUT_CLASS} max-w-xs font-mono`}
                data-testid="account-id-input"
              />
            )}
          </div>
          <Textarea
            value={statusForm.note}
            onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })}
            rows={3}
            maxLength={2000}
            placeholder={
              selectedNeedsNote
                ? "Required: tell the organizer exactly what to fix (they get this by email)"
                : "Optional note (shown to the organizer)"
            }
            className="rounded-md border-zinc-200 focus-visible:ring-black"
            data-testid="status-note"
          />
          {statusForm.status === "connected" && !gateway?.connected && (
            <p className="text-xs text-amber-700">
              Use “Connect keys for this organizer” below instead; Connected needs working keys.
            </p>
          )}
          <Button
            type="submit"
            disabled={savingStatus || !statusForm.status || (selectedNeedsNote && !statusForm.note.trim())}
            className="h-10 rounded-md bg-zinc-900 text-white hover:bg-zinc-700"
            data-testid="status-save"
          >
            {savingStatus && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Update status
          </Button>
        </form>
      </Panel>

      <Panel
        title="Webhook"
        subtitle="Add this in the client's Razorpay account: Account & Settings → Webhooks → Add New Webhook"
        action={
          <Button
            type="button"
            variant="outline"
            onClick={generateWebhookSecret}
            disabled={generating}
            className="h-9 rounded-md"
            data-testid="generate-webhook-secret"
          >
            {generating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1.5 h-4 w-4" />}
            {detail.webhook?.secret_generated_at ? "Generate a new secret" : "Generate webhook secret"}
          </Button>
        }
      >
        <CopyRow label="Webhook URL" value={webhook?.webhook_url || detail.webhook?.url} />
        <CopyRow label="Active event" value={(webhook?.webhook_events || detail.webhook?.events || []).join(", ")} />
        <CopyRow
          label="Secret"
          sensitive
          masked={detail.webhook?.secret_generated_at ? `generated ${formatDateTime(detail.webhook.secret_generated_at)}` : "not generated yet"}
          revealedValue={webhookSecret}
          testId="row-webhook-secret"
        />
        {detail.webhook?.secret_generated_at && !webhookSecret && (
          <p className="mt-2 text-xs text-zinc-500">Reveal for copying (above) to see the saved secret.</p>
        )}
        {webhook && (
          <p className="mt-2 text-xs text-amber-700">
            A new secret replaces the old one. If you already added the webhook, update its secret in Razorpay too.
          </p>
        )}
      </Panel>

      <Panel
        title="Connect keys for this organizer"
        subtitle="Live keys from the client's account. They are verified with Razorpay, then saved exactly as if the organizer connected them."
        testId="connect-keys-panel"
      >
        {gateway?.connected && (
          <p className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-800">
            <ShieldCheck className="h-4 w-4" /> Connected · {gateway.key_id_masked}
            {gateway.key_env === "test" && " (TEST keys)"} · since {formatDateTime(gateway.connected_at)}
          </p>
        )}
        {!detail.can_connect_gateway ? (
          <p className="text-sm text-zinc-500">
            Available once the Razorpay account is created (mark “Razorpay account created” first).
          </p>
        ) : (
          <form onSubmit={connectKeys} className="grid gap-3 sm:grid-cols-2">
            <Input
              required
              value={connectForm.key_id}
              onChange={(e) => setConnectForm({ ...connectForm, key_id: e.target.value })}
              placeholder="Key ID (rzp_live_…)"
              autoComplete="off"
              className={INPUT_CLASS}
              data-testid="connect-key-id"
            />
            <Input
              required
              type="password"
              value={connectForm.key_secret}
              onChange={(e) => setConnectForm({ ...connectForm, key_secret: e.target.value })}
              placeholder="Key secret"
              autoComplete="new-password"
              className={INPUT_CLASS}
              data-testid="connect-key-secret"
            />
            <Input
              value={connectForm.webhook_secret}
              onChange={(e) => setConnectForm({ ...connectForm, webhook_secret: e.target.value })}
              placeholder={
                detail.webhook?.secret_generated_at
                  ? "Webhook secret (empty = use the generated one)"
                  : "Webhook secret (generate one above)"
              }
              autoComplete="off"
              className={`${INPUT_CLASS} sm:col-span-2`}
              data-testid="connect-webhook-secret"
            />
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={connecting}
                className="h-10 rounded-md bg-zinc-900 text-white hover:bg-zinc-700"
                data-testid="connect-keys-button"
              >
                {connecting ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Verifying with Razorpay…
                  </>
                ) : gateway?.connected ? (
                  "Replace keys"
                ) : (
                  "Connect keys"
                )}
              </Button>
            </div>
          </form>
        )}
      </Panel>

      <Panel title="Audit trail" subtitle="Newest first. Kept even after documents are deleted.">
        <ul className="space-y-1.5 text-sm" data-testid="audit-trail">
          {(detail.audit || []).map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="flex flex-wrap gap-x-3">
              <span className="w-40 shrink-0 text-xs text-zinc-400">{formatDateTime(entry.at)}</span>
              <span className="min-w-0 flex-1 text-zinc-700">
                {describeAudit(entry)}
                {entry.note && <span className="block text-xs text-zinc-500">“{entry.note}”</span>}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export default function PayoutSetupRequests() {
  const [filter, setFilter] = useState("open");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/super-admin/payout-setup", { params: { status: filter } });
      setData(response.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  if (selectedId) {
    return (
      <RequestDetail
        organizerId={selectedId}
        onBack={() => setSelectedId(null)}
        onChanged={loadList}
      />
    );
  }

  const counts = data?.counts || {};
  const openCount = Object.entries(counts)
    .filter(([status]) => !["connected", "rejected"].includes(status))
    .reduce((sum, [, n]) => sum + n, 0);
  const allCount = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const countFor = (key) => (key === "open" ? openCount : key === "all" ? allCount : counts[key] || 0);

  return (
    <div data-testid="payout-requests">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              filter === key
                ? "border-zinc-900 bg-zinc-900 text-white"
                : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
            }`}
          >
            {key === "open" ? "Open" : key === "all" ? "All" : STATUS_LABEL[key]} · {countFor(key)}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="pb-3 pr-4">Organizer</th>
              <th className="pb-3 pr-4">Submitted</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3 pr-4">Age</th>
              <th className="pb-3">In this status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="5" className="py-8 text-center text-zinc-500">Loading requests...</td>
              </tr>
            )}
            {!loading && !data?.requests?.length && (
              <tr>
                <td colSpan="5" className="py-8 text-center text-zinc-500">No requests here.</td>
              </tr>
            )}
            {!loading &&
              data?.requests?.map((row) => (
                  <tr
                    key={row.organizer_id}
                    className="cursor-pointer border-b border-zinc-100 hover:bg-zinc-50"
                    onClick={() => setSelectedId(row.organizer_id)}
                    data-testid="payout-request-row"
                  >
                    <td className="py-3 pr-4">
                      <div className="font-medium text-zinc-900">
                        {row.customer_facing_business_name || row.legal_business_name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {row.organizer_email} · {row.business_type_label}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">{formatDateTime(row.submitted_at)}</td>
                    <td className="py-3 pr-4">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">{formatAge(row.age_seconds)}</td>
                    <td className="py-3 text-zinc-600">{formatAge(row.status_age_seconds)}</td>
                  </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
